import type { Ref } from 'vue';
import type { MessageApi } from 'naive-ui';
import type {
  BlendPointRef,
  ChamferFeatureDefinition,
  FilletFeatureDefinition,
} from '@puppycad/cad-core';
import type { ExtrudeViewportRef, ViewportSelectionSnapshot } from '../extrude-types';
import {
  buildBlendPartialReferencePointFromObject,
  buildBlendSelectionFromObject,
  buildBlendSelectionFromTopologyPick,
  isBlendSelectableObject,
  resolveBlendSourceMesh,
} from '@/core/features/blend/selection';
import { flattenFaceSetItems, type ChamferSelectionTarget, type FilletSelectionTarget } from '@/core/features/blend/stage-two';
import type { BlendSelectionDisplayItem } from '@/core/features/blend/types';
import type { ActiveBlendKind, BlendObject } from './blend-feature-helpers';
import {
  buildBlendObjectDedupKey,
  buildBlendSelectionItemDedupKey,
  resolveBlendPointIdentityKey,
  resolveBlendUpstreamBodyObjectId,
} from './blend-feature-helpers';
import type { BlendPartialReferenceTarget } from '@/core/features/blend/partial-edge';

type BlendSelectionCandidate = {
  object: BlendObject | null;
  selection: {
    itemRef: import('@puppycad/cad-core').SelectionItemRef;
    displayItem: BlendSelectionDisplayItem;
  };
};

interface BlendSelectionFlowContext {
  viewportRef: ExtrudeViewportRef;
  activeKind: Ref<ActiveBlendKind>;
  filletDefinition: Ref<FilletFeatureDefinition>;
  chamferDefinition: Ref<ChamferFeatureDefinition>;
  filletSelectionTarget: Ref<FilletSelectionTarget>;
  chamferSelectionTarget: Ref<ChamferSelectionTarget>;
  filletPartialReferenceTarget: Ref<BlendPartialReferenceTarget | null>;
  chamferPartialReferenceTarget: Ref<BlendPartialReferenceTarget | null>;
  message: MessageApi;
}

/**
 * 创建圆角/倒角选择流程辅助函数。
 *
 * @param context 选择流程上下文。
 * @returns 选择流程辅助函数集合。
 */
export function createBlendSelectionFlowHelpers(context: BlendSelectionFlowContext) {
  const {
    viewportRef,
    activeKind,
    filletDefinition,
    chamferDefinition,
    filletSelectionTarget,
    chamferSelectionTarget,
    filletPartialReferenceTarget,
    chamferPartialReferenceTarget,
    message,
  } = context;
  const getPrimaryBlendObject = (): BlendObject | null => {
    const object = viewportRef.value?.getPrimarySelectedObject?.() ?? null;
    return isBlendSelectableObject(object) ? object : null;
  };

  /**
   * 从事件快照与实时视口中解析圆角/倒角可消费的选择对象。
   *
   * 设计说明：
   * 1. `SelectionUpdated` 已经提供了稳定快照，宿主不能再只依赖二次回读的 primary object；
   * 2. 某些边选择命中在事件回调时会短暂退化，快照里的 `selectedObjects` 仍然保留了真实边对象；
   * 3. 这里统一做去重与优先级收口，避免 UI 宿主各处自行拼装选择来源。
   *
   * @param snapshot 事件快照。
   * @returns 按优先级排序后的可消费选择对象。
   */
  const resolveSelectionCandidates = (snapshot?: ViewportSelectionSnapshot): BlendSelectionCandidate[] => {
    const orderedCandidates: BlendSelectionCandidate[] = [];
    const acceptedKeys = new Set<string>();

    /**
     * 优先消费 cad-core 直接产出的权威拓扑命中快照，避免 web-client 再从 Object3D 反推引用。
     */
    const pushResolvedCandidate = (candidate: BlendSelectionCandidate | null): void => {
      if (!candidate) {
        return;
      }
      const dedupKey = buildBlendSelectionItemDedupKey(candidate.selection.itemRef);
      if (acceptedKeys.has(dedupKey)) {
        return;
      }
      acceptedKeys.add(dedupKey);
      orderedCandidates.push(candidate);
    };

    /**
     * 只接纳 cad-core 暴露的权威拓扑代理对象，作为缺少权威快照时的兼容回退路径。
     */
    const pushObjectCandidate = (object: unknown): void => {
      if (!isBlendSelectableObject(object)) {
        return;
      }
      const dedupKey = buildBlendObjectDedupKey(object);
      if (acceptedKeys.has(dedupKey)) {
        return;
      }
      const selection = buildBlendSelectionFromObject(object);
      if (!selection) {
        return;
      }
      acceptedKeys.add(dedupKey);
      orderedCandidates.push({
        object,
        selection,
      });
    };

    /**
     * 若当前回调已经持有 SelectionUpdated 快照，宿主必须只信任该快照。
     * 否则一旦底层选择尚未完成清理，回读实时 selection 就会把上一轮旧主选重新吸收回来。
     */
    if (snapshot) {
      const snapshotObjects = new Map<string, BlendObject>();
      snapshot.selectedObjects.forEach((object) => {
        if (isBlendSelectableObject(object)) {
          snapshotObjects.set(object.uuid, object);
        }
      });
      const snapshotObject = snapshot.object;
      if (isBlendSelectableObject(snapshotObject)) {
        snapshotObjects.set(snapshotObject.uuid, snapshotObject);
      }

      const topologyPicks = [
        ...(snapshot.topologyPick ? [snapshot.topologyPick] : []),
        ...(snapshot.topologyPicks ?? []),
      ];
      topologyPicks.forEach((pick) => {
        const selection = buildBlendSelectionFromTopologyPick(pick);
        const sceneObject = viewportRef.value?.getSceneObjectById?.(pick.sceneObjectId) ?? null;
        const candidateObject = snapshotObjects.get(pick.sceneObjectId)
          ?? (isBlendSelectableObject(sceneObject) ? sceneObject : null);
        pushResolvedCandidate(selection ? {
          object: candidateObject,
          selection,
        } : null);
      });
      if (orderedCandidates.length > 0) {
        return orderedCandidates;
      }

      pushObjectCandidate(snapshot.object ?? null);
      snapshot.selectedObjects.forEach((object) => pushObjectCandidate(object));
      return orderedCandidates;
    }

    (viewportRef.value?.getSelectedObjects?.() ?? []).forEach((object) => pushObjectCandidate(object));
    pushObjectCandidate(viewportRef.value?.getPrimarySelectedObject?.() ?? null);

    return orderedCandidates;
  };

  /**
   * 判断当前是否处于部分边线参考点选择状态。
   *
   * @returns 当前参考目标；未激活时返回 `null`。
   */
  const resolveActivePartialReferenceTarget = (): {
    kind: Exclude<ActiveBlendKind, null>;
    target: BlendPartialReferenceTarget;
  } | null => {
    if (
      activeKind.value === 'fillet'
      && filletSelectionTarget.value === 'partial-edge-reference'
      && filletPartialReferenceTarget.value
    ) {
      return { kind: 'fillet', target: filletPartialReferenceTarget.value };
    }
    if (
      activeKind.value === 'chamfer'
      && chamferSelectionTarget.value === 'partial-edge-reference'
      && chamferPartialReferenceTarget.value
    ) {
      return { kind: 'chamfer', target: chamferPartialReferenceTarget.value };
    }
    return null;
  };

  /**
   * 从事件快照与实时视口中解析部分边线参考点。
   *
   * 设计说明：
   * 1. reference-offset 在当前项目中只支持点，因此这里不再走通用边/面/点选择槽；
   * 2. 参考点可以来自其它草图，不能触发同实体校验；
   * 3. 快照存在时优先使用快照，避免内部选择重放时误吸收旧对象。
   *
   * @param snapshot 选择事件快照。
   * @returns 按优先级排序后的参考点集合。
   */
  const resolvePartialReferencePointCandidates = (snapshot?: ViewportSelectionSnapshot): BlendPointRef[] => {
    const candidates: BlendPointRef[] = [];
    const acceptedKeys = new Set<string>();
    const pushPointObject = (object: unknown): void => {
      const pointRef = buildBlendPartialReferencePointFromObject(object);
      if (!pointRef) {
        return;
      }
      const key = resolveBlendPointIdentityKey(pointRef);
      if (acceptedKeys.has(key)) {
        return;
      }
      acceptedKeys.add(key);
      candidates.push(pointRef);
    };

    if (snapshot) {
      pushPointObject(snapshot.object ?? null);
      snapshot.selectedObjects.forEach((object) => pushPointObject(object));
      [
        ...(snapshot.topologyPick ? [snapshot.topologyPick] : []),
        ...(snapshot.topologyPicks ?? []),
      ].forEach((pick) => pushPointObject(viewportRef.value?.getSceneObjectById?.(pick.sceneObjectId) ?? null));
      return candidates;
    }

    (viewportRef.value?.getSelectedObjects?.() ?? []).forEach((object) => pushPointObject(object));
    pushPointObject(viewportRef.value?.getPrimarySelectedObject?.() ?? null);
    return candidates;
  };

  /**
   * 校验当前候选选择是否与已绑定实体一致。
   *
   * 设计说明：
   * 1. 只读校验，不在此处写入 `upstreamBodyObjectId`；
   * 2. 主选择集为空时不做实体限制，清空选择后允许切换到另一个实体；
   * 3. 真正写入上游 ID 由 definition 变更后的 `syncUpstreamBodyObjectId` 统一推导。
   *
   * @param bodyObjectId 候选拓扑项所属实体 ID。
   * @returns 是否允许继续吸收该选择。
   */
  const ensureSameBody = (bodyObjectId: string): boolean => {
    if (!activeKind.value) {
      return true;
    }

    const lockedBodyObjectId = resolveBlendUpstreamBodyObjectId(
      activeKind.value,
      filletDefinition.value,
      chamferDefinition.value,
    );
    if (!lockedBodyObjectId || lockedBodyObjectId === bodyObjectId) {
      return true;
    }

    message.warning('同一个圆角或倒角特征只能作用于同一个实体。');
    return false;
  };

  /**
   * 判断当前选择项是否已经被当前命令吸收。
   *
   * 设计说明：
   * 1. 圆角/倒角切换选择模式时，底层会把“保留中的当前选择”重新广播一次；
   * 2. 宿主必须能识别这是不是一次内部重放，而不是把已吸收对象当成重复点击去反向移除；
   * 3. 这里统一按定义模型真值判断，避免 UI 列表顺序变化造成误判。
   *
   * @param itemRef 当前选择项。
   * @returns 是否已被当前命令吸收。
   */
  const isSelectionAlreadyAbsorbed = (itemRef: { persistentRef: string }): boolean => {
    if (activeKind.value === 'fillet') {
      if (filletSelectionTarget.value === 'partial-edge-reference') {
        const target = filletPartialReferenceTarget.value;
        const partialEdge = (filletDefinition.value.partialEdges ?? [])
          .find((item) => target && item.itemRef === target.itemRef);
        const referencePoint = target?.side === 'start'
          ? partialEdge?.startReferencePoint
          : partialEdge?.endReferencePoint;
        return referencePoint?.persistentRef === itemRef.persistentRef;
      }
      if (filletSelectionTarget.value === 'help-point') {
        return filletDefinition.value.helpPoint?.persistentRef === itemRef.persistentRef;
      }
      if (filletDefinition.value.type === 'face') {
        return flattenFaceSetItems(filletDefinition.value.faceSets).some((item) => item.persistentRef === itemRef.persistentRef);
      }
      return (filletDefinition.value.items ?? []).some((item) => item.persistentRef === itemRef.persistentRef);
    }

    if (chamferSelectionTarget.value === 'partial-edge-reference') {
      const target = chamferPartialReferenceTarget.value;
      const partialEdge = (chamferDefinition.value.partialEdges ?? [])
        .find((item) => target && item.itemRef === target.itemRef);
      const referencePoint = target?.side === 'start'
        ? partialEdge?.startReferencePoint
        : partialEdge?.endReferencePoint;
      return referencePoint?.persistentRef === itemRef.persistentRef;
    }
    if (chamferSelectionTarget.value === 'help-point') {
      return chamferDefinition.value.helpPoint?.persistentRef === itemRef.persistentRef;
    }
    if (chamferDefinition.value.type === 'face-face') {
      return flattenFaceSetItems(chamferDefinition.value.faceSets)
        .some((item) => item.persistentRef === itemRef.persistentRef);
    }
    return (chamferDefinition.value.items ?? []).some((item) => item.persistentRef === itemRef.persistentRef);
  };

  /**
   * 判断参考点是否已经写入当前激活的部分边线字段。
   *
   * @param pointRef 参考点。
   * @returns 是否已经吸收。
   */
  const isPartialReferencePointAlreadyAbsorbed = (pointRef: BlendPointRef): boolean => {
    const activeTarget = resolveActivePartialReferenceTarget();
    if (!activeTarget) {
      return false;
    }
    const partialEdge = activeTarget.kind === 'fillet'
      ? (filletDefinition.value.partialEdges ?? []).find((item) => item.itemRef === activeTarget.target.itemRef)
      : (chamferDefinition.value.partialEdges ?? []).find((item) => item.itemRef === activeTarget.target.itemRef);
    const referencePoint = activeTarget.target.side === 'start'
      ? partialEdge?.startReferencePoint
      : partialEdge?.endReferencePoint;
    return Boolean(referencePoint)
      && resolveBlendPointIdentityKey(referencePoint) === resolveBlendPointIdentityKey(pointRef);
  };


  return {
    getPrimaryBlendObject,
    resolveSelectionCandidates,
    resolveActivePartialReferenceTarget,
    resolvePartialReferencePointCandidates,
    ensureSameBody,
    isSelectionAlreadyAbsorbed,
    isPartialReferencePointAlreadyAbsorbed,
  };
}
